import React, { useState, useEffect, useCallback } from 'react';
import RichTextEditor from '../../common/RichTextEditor';
import {
  X,
  FileText,
  User,
  Users,
  Tag,
  Upload,
  Download,
  Trash2,
  MessageSquare,
  Clock,
  Edit3,
  AlertCircle,
  CheckCircle,
  Paperclip,
  ExternalLink,
  FileIcon,
  DollarSign,
  File,
  Shield,
  TrendingUp,
  Mail,
  Copy,
  Check
} from 'lucide-react';
import {
  ContractAnalysisJSON,
  ReviewClause
} from '../../../types/contract.types';
import { PermissionGuard } from '../../../hooks/usePermissions';
import { contractService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { decodeHtmlEntities } from '../../../utils/textUtils';
import { downloadContractFile } from '../../../utils/downloadContractFile';

// Helper function to validate ObjectId format
const isValidObjectId = (id: string): boolean => {
  return /^[0-9a-fA-F]{24}$/.test(id);
};


// For date-fns v4, we'll use a simple date formatter for now
const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  }).format(date);
};

const formatRelativeTime = (date: Date) => {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInMinutes < 1) return 'just now';
  if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
  if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  if (diffInDays < 30) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  
  return formatDate(date);
};

interface ContractFile {
  id: string;
  filename: string;
  fileSize: number;
  mimeType: string;
  uploadedBy: {
    id: string;
    name: string;
    email: string;
  };
  uploadedAt: Date;
  version: string;
  versionLabel?: string;
  versionType?: 'draft' | 'signed' | 'internal' | 'redline';
  notes?: string;
  downloadUrl?: string;
}

interface ContractNote {
  id: string;
  content: string;
  createdBy: {
    id: string;
    name: string;
    email: string;
  };
  createdAt: Date;
  updatedAt?: Date;
  isEdited: boolean;
}

interface Contract {
  id: string;
  _id?: string; // MongoDB ObjectId for backend operations
  contractNumber: string;
  title: string;
  description?: string;
  supplier: {
    id: string;
    name: string;
  };
  startDate: Date;
  endDate: Date;
  value: number;
  currency: string;
  status: 'draft' | 'active' | 'pending' | 'expired' | 'terminated';
  category: string;
  createdBy: {
    id: string;
    name: string;
  };
  createdAt: Date;
  updatedAt: Date;
  negotiationPoints?: Array<{
    id: string;
    title: string;
    description: string;
    status: 'pending' | 'agreed' | 'rejected';
    priority: 'high' | 'medium' | 'low';
  }>;
  files?: ContractFile[];
  notes?: ContractNote[];
  // Analysis data
  jsonAnalysis?: ContractAnalysisJSON;
  clauseDecisions?: Record<string, { status: string; comments: any[]; lastUpdatedBy?: string; lastUpdatedAt?: Date }>;
  reviewClauses?: ReviewClause[];
  // New fields
  minPurchaseAmount?: number;
  marketingBudget?: number;
  renewalTermsCount?: number;
  incotermTerms?: string;
  paymentCurrency?: string;
  // Negotiation email
  negotiationEmail?: {
    subject: string;
    body: string;
    generatedAt: string;
    generatedBy?: string;
  };
}

interface ContractDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract: Contract | null;
  onEdit?: (contract: Contract) => void;
  onReview?: (contract: Contract) => void;
  onFileUpload?: (contractId: string, file: File, versionInfo: any) => Promise<void>;
  onFileDelete?: (contractId: string, fileId: string) => Promise<void>;
  onNoteAdd?: (contractId: string, content: string) => Promise<void>;
  onNoteEdit?: (contractId: string, noteId: string, content: string) => Promise<void>;
  onNoteDelete?: (contractId: string, noteId: string) => Promise<void>;
}

const ContractDetailsModal: React.FC<ContractDetailsModalProps> = ({
  isOpen,
  onClose,
  contract,
  onEdit,
  onReview,
  onFileUpload,
  onFileDelete,
  onNoteAdd,
  onNoteEdit,
  onNoteDelete
}) => {
  const { token } = useAuth();
  const [activeSection, setActiveSection] = useState<'info' | 'files' | 'notes' | 'negotiation'>('info');
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [files, setFiles] = useState<ContractFile[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [notes, setNotes] = useState<ContractNote[]>([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    file: null as File | null,
    versionLabel: '',
    versionType: 'draft' as 'draft' | 'signed' | 'internal' | 'redline',
    notes: ''
  });
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');

  // Fetch files for the contract
  const fetchContractFiles = useCallback(async () => {
    if (!contract || !token) return;
    
    setIsLoadingFiles(true);
    try {
      // Try to get a valid ObjectId - prioritize _id, but also check if id is already ObjectId format
      let contractId = contract._id;
      
      // If no _id, check if the id field might already be an ObjectId
      if (!contractId && contract.id) {
        const idStr = contract.id.toString();
        if (isValidObjectId(idStr)) {
          contractId = idStr;
        }
      }
      
      if (!contractId) {
        console.warn('Contract object does not have a valid ObjectId. Contract data:', {
          id: contract.id,
          _id: contract._id,
          contractNumber: contract.contractNumber,
          title: contract.title
        });
        setFiles([]);
        setIsLoadingFiles(false);
        return;
      }
      
      if (!isValidObjectId(contractId)) {
        console.error('Invalid ObjectId format for contract:', contractId);
        setFiles([]);
        setIsLoadingFiles(false);
        return;
      }
      
      const filesData = await contractService.getFiles(token, contractId);
      // Transform backend files to frontend format
      const transformedFiles = filesData.map((file: any) => ({
        id: file._id || file.id,
        filename: file.originalName,
        fileSize: file.fileSize,
        mimeType: file.mimeType,
        uploadedBy: {
          id: file.uploadedBy._id || file.uploadedBy.id,
          name: `${file.uploadedBy.firstName} ${file.uploadedBy.lastName}`,
          email: file.uploadedBy.email
        },
        uploadedAt: new Date(file.createdAt),
        version: file.version,
        versionLabel: file.versionLabel,
        versionType: file.versionType,
        notes: file.notes,
        // Don't store downloadUrl anymore - we'll handle download via function
      }));
      setFiles(transformedFiles);
    } catch (error) {
      console.error('Failed to fetch contract files:', error);
      setFiles([]);
    } finally {
      setIsLoadingFiles(false);
    }
  }, [contract, token]);

  // Fetch notes for the contract
  const fetchContractNotes = useCallback(async () => {
    if (!contract || !token) return;
    
    setIsLoadingNotes(true);
    try {
      // Prioritize contract._id for backend operations
      const contractId = contract._id || contract.id;
      
      if (!contractId || !isValidObjectId(contractId)) {
        console.error('Invalid contract ID for fetching notes:', contractId);
        setNotes([]);
        return;
      }
      
      const notesData = await contractService.getNotes(token, contractId);
      setNotes(notesData || []);
    } catch (error) {
      console.error('Failed to fetch contract notes:', error);
      setNotes([]);
    } finally {
      setIsLoadingNotes(false);
    }
  }, [contract, token]);

  // Handle file download with authentication
  const handleDownloadFile = async (fileId: string, filename: string) => {
    if (!token) {
      console.error('No authentication token available');
      return;
    }

    try {
      await downloadContractFile(token, fileId, filename);
    } catch (error) {
      console.error('File download failed:', error);
      // You might want to show an error message to the user here
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setActiveSection('info');
      setUploadForm({
        file: null,
        versionLabel: '',
        versionType: 'draft',
        notes: ''
      });
      setNewNote('');
      setEditingNoteId(null);
      setFiles([]);
      setNotes([]);
    } else if (isOpen && contract) {
      // Fetch files and notes when modal opens
      fetchContractFiles();
      fetchContractNotes();
    }
  }, [isOpen, contract, fetchContractFiles, fetchContractNotes]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !contract) return null;

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-400',
      pending: 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-400',
      draft: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
      expired: 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-400',
      terminated: 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-400'
    };
    return colors[status as keyof typeof colors] || colors.draft;
  };

  const getVersionTypeColor = (type: string) => {
    const colors = {
      draft: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
      signed: 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-400',
      internal: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
      redline: 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-400'
    };
    return colors[type as keyof typeof colors] || colors.draft;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadForm({ ...uploadForm, file });
    }
  };

  const handleFileUpload = async () => {
    if (!uploadForm.file || !onFileUpload) return;
    
    setIsUploadingFile(true);
    try {
      await onFileUpload(contract.id, uploadForm.file, {
        versionLabel: uploadForm.versionLabel,
        versionType: uploadForm.versionType,
        notes: uploadForm.notes
      });
      setUploadForm({
        file: null,
        versionLabel: '',
        versionType: 'draft',
        notes: ''
      });
      // Refresh files list after successful upload
      await fetchContractFiles();
    } catch (error) {
      console.error('File upload failed:', error);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleNoteAdd = async () => {
    // Check if note has meaningful content (not just HTML tags)
    const textContent = newNote.replace(/<[^>]*>/g, '').trim();
    if (!textContent || !onNoteAdd) return;
    
    try {
      await onNoteAdd(contract.id, newNote);
      setNewNote('');
      // Refresh notes list to show the new note
      await fetchContractNotes();
    } catch (error) {
      console.error('Failed to add note:', error);
    }
  };

  const handleNoteEdit = async (noteId: string) => {
    // Check if note has meaningful content (not just HTML tags)
    const textContent = editingNoteContent.replace(/<[^>]*>/g, '').trim();
    if (!textContent || !onNoteEdit) return;
    
    try {
      await onNoteEdit(contract.id, noteId, editingNoteContent);
      setEditingNoteId(null);
      setEditingNoteContent('');
      // Refresh notes list to show the updated note
      await fetchContractNotes();
    } catch (error) {
      console.error('Failed to edit note:', error);
    }
  };


  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl dark:shadow-dark-lg border border-gray-200/50 dark:border-dark-border max-w-6xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Clean Header */}
        <div className="relative px-6 py-5 bg-gray-50 dark:bg-dark-bg-secondary border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-gradient-to-br from-primary-500 to-primary-600 dark:from-accent-400 dark:to-accent-500 rounded-xl flex items-center justify-center shadow-md">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{decodeHtmlEntities(contract.title || (contract as any).contractTitle)}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Contract Details & Management</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-surface-raised transition-colors"
              title="Close (ESC)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Clean Navigation Tabs */}
        <div className="px-6 py-3 bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border">
          <nav className="flex space-x-1">
            <button
              onClick={() => setActiveSection('info')}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
                activeSection === 'info'
                  ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-bg-secondary'
              }`}
            >
              <FileText className="w-4 h-4" />
              Contract Info
            </button>
            <PermissionGuard permission="contracts.files.view">
              <button
                onClick={() => setActiveSection('files')}
                className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
                  activeSection === 'files'
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-bg-secondary'
                }`}
              >
                <Upload className="w-4 h-4" />
                Files & Versions
                {files && files.length > 0 && (
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    activeSection === 'files'
                      ? 'bg-primary-200/50 dark:bg-primary-800/50 text-primary-700 dark:text-primary-300'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}>
                    {files.length}
                  </span>
                )}
              </button>
            </PermissionGuard>
            <PermissionGuard permission="contracts.notes.view">
              <button
                onClick={() => setActiveSection('notes')}
                className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
                  activeSection === 'notes'
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-bg-secondary'
                }`}
              >
                <MessageSquare className="w-4 h-4" />
                Internal Notes
                {notes && notes.length > 0 && (
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    activeSection === 'notes'
                      ? 'bg-primary-200/50 dark:bg-primary-800/50 text-primary-700 dark:text-primary-300'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}>
                    {notes.length}
                  </span>
                )}
              </button>
            </PermissionGuard>
            {(contract as any).negotiationEmail && (
              <button
                onClick={() => setActiveSection('negotiation')}
                className={`px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2 ${
                  activeSection === 'negotiation'
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-bg-secondary'
                }`}
              >
                <Mail className="w-4 h-4" />
                Negotiation Email
              </button>
            )}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 bg-gray-50 dark:bg-dark-bg">
          {activeSection === 'info' && (
            <div className="space-y-6">
              {/* Contract Header Card */}
              <div className="bg-white dark:bg-dark-surface rounded-xl p-5 border border-gray-200 dark:border-dark-border">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary-50 dark:bg-primary-900/30 rounded-xl flex items-center justify-center">
                      <FileText className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{decodeHtmlEntities(contract.title || (contract as any).contractTitle)}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Contract ID: {contract.contractNumber || 'Auto-generated'}</p>
                      {contract.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-3 leading-relaxed">{contract.description}</p>
                      )}
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${getStatusColor(contract.status)}`}>
                    {contract.status === 'active' && <CheckCircle className="w-4 h-4" />}
                    {contract.status === 'pending' && <Clock className="w-4 h-4" />}
                    {contract.status === 'draft' && <FileText className="w-4 h-4" />}
                    {(contract.status === 'expired' || contract.status === 'terminated') && <AlertCircle className="w-4 h-4" />}
                    {contract.status.charAt(0).toUpperCase() + contract.status.slice(1)}
                  </span>
                </div>
              </div>

              {/* Financial Overview */}
              <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-dark-border">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-primary-500 dark:text-accent-400" />
                    Financial Overview
                  </h4>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Contract Value */}
                    <div className="p-4 bg-gray-50 dark:bg-dark-bg-secondary rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Contract Value</span>
                      </div>
                      <p className="text-xl font-semibold text-gray-900 dark:text-white">
                        {(contract as any).paymentCurrency || contract.currency || 'USD'} {contract.value.toLocaleString()}
                      </p>
                    </div>

                    {/* Min Purchase Amount */}
                    <div className="p-4 bg-gray-50 dark:bg-dark-bg-secondary rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Min Purchase</span>
                      </div>
                      <p className="text-xl font-semibold text-gray-900 dark:text-white">
                        {contract.minPurchaseAmount
                          ? `${contract.paymentCurrency || 'USD'} ${contract.minPurchaseAmount.toLocaleString()}`
                          : <span className="text-gray-400 dark:text-gray-500">-</span>
                        }
                      </p>
                    </div>

                    {/* Marketing Budget */}
                    <div className="p-4 bg-gray-50 dark:bg-dark-bg-secondary rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Marketing Budget</span>
                      </div>
                      <p className="text-xl font-semibold text-gray-900 dark:text-white">
                        {contract.marketingBudget
                          ? `${contract.paymentCurrency || 'USD'} ${contract.marketingBudget.toLocaleString()}`
                          : <span className="text-gray-400 dark:text-gray-500">-</span>
                        }
                      </p>
                    </div>

                    {/* Payment Currency */}
                    <div className="p-4 bg-gray-50 dark:bg-dark-bg-secondary rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Currency</span>
                      </div>
                      <p className="text-xl font-semibold text-gray-900 dark:text-white">
                        {contract.paymentCurrency || <span className="text-gray-400 dark:text-gray-500">-</span>}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contract Details */}
              <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-dark-border">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Clock className="w-4 h-4 text-primary-500 dark:text-accent-400" />
                    Contract Details
                  </h4>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Duration */}
                    <div className="p-4 bg-gray-50 dark:bg-dark-bg-secondary rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Duration</span>
                      </div>
                      <p className="text-xl font-semibold text-gray-900 dark:text-white">
                        {Math.ceil((new Date(contract.endDate).getTime() - new Date(contract.startDate).getTime()) / (1000 * 60 * 60 * 24 * 30))} months
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formatDate(new Date(contract.startDate))} - {formatDate(new Date(contract.endDate))}</p>
                    </div>

                    {/* Category */}
                    <div className="p-4 bg-gray-50 dark:bg-dark-bg-secondary rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Tag className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Category</span>
                      </div>
                      <p className="text-xl font-semibold text-gray-900 dark:text-white">{contract.category}</p>
                    </div>

                    {/* Renewal Terms */}
                    <div className="p-4 bg-gray-50 dark:bg-dark-bg-secondary rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Renewal Terms</span>
                      </div>
                      <p className="text-xl font-semibold text-gray-900 dark:text-white">
                        {contract.renewalTermsCount || <span className="text-gray-400 dark:text-gray-500">-</span>}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-dark-border">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary-500 dark:text-accent-400" />
                    Terms & Conditions
                  </h4>
                </div>
                <div className="p-5">
                  <div className="p-4 bg-gray-50 dark:bg-dark-bg-secondary rounded-lg">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Incoterm Terms</span>
                    <div className="mt-2">
                      {contract.incotermTerms ? (
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{contract.incotermTerms}</p>
                      ) : (
                        <p className="text-sm text-gray-400 dark:text-gray-500">No data available</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Supplier Information */}
              <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-dark-border">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary-500 dark:text-accent-400" />
                    Supplier Information
                  </h4>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-primary-50 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Company Name</span>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">{contract.supplier.name}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'files' && (
            <PermissionGuard permission="contracts.files.view">
              <div className="space-y-6">
              {/* Upload Section */}
              <PermissionGuard permission="contracts.files.upload">
                <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border">
                  <div className="px-5 py-4 border-b border-gray-200 dark:border-dark-border">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <Upload className="w-4 h-4 text-primary-500 dark:text-accent-400" />
                      Upload Contract Version
                    </h3>
                  </div>
                  <div className="p-5 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Select File
                      </label>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.xlsx"
                        onChange={handleFileSelect}
                        className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 dark:file:bg-primary-900/30 file:text-primary-700 dark:file:text-primary-300 hover:file:bg-primary-100 dark:hover:file:bg-primary-900/50 file:cursor-pointer"
                      />
                      {uploadForm.file && (
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                          Selected: {uploadForm.file.name} ({formatFileSize(uploadForm.file.size)})
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Version Label
                        </label>
                        <input
                          type="text"
                          value={uploadForm.versionLabel}
                          onChange={(e) => setUploadForm({ ...uploadForm, versionLabel: e.target.value })}
                          placeholder="e.g., v2 - Legal Review"
                          className="w-full px-3 py-2.5 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-accent-400/20 focus:border-primary-500 dark:focus:border-accent-400 placeholder-gray-400 dark:placeholder-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Version Type
                        </label>
                        <select
                          value={uploadForm.versionType}
                          onChange={(e) => setUploadForm({ ...uploadForm, versionType: e.target.value as any })}
                          className="w-full px-3 py-2.5 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-accent-400/20 focus:border-primary-500 dark:focus:border-accent-400"
                        >
                          <option value="draft">Draft</option>
                          <option value="signed">Signed</option>
                          <option value="redline">Redline</option>
                          <option value="internal">Internal</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Notes (Optional)
                      </label>
                      <textarea
                        value={uploadForm.notes}
                        onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2.5 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-accent-400/20 focus:border-primary-500 dark:focus:border-accent-400 placeholder-gray-400 dark:placeholder-gray-500"
                        placeholder="Add any relevant notes about this version..."
                      />
                    </div>

                    <button
                      onClick={handleFileUpload}
                      disabled={!uploadForm.file || isUploadingFile}
                      className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 rounded-lg hover:shadow-lg hover:shadow-primary-500/25 dark:hover:shadow-accent-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                    >
                      <Upload className="w-4 h-4" />
                      {isUploadingFile ? 'Uploading...' : 'Upload File'}
                    </button>
                  </div>
                </div>
              </PermissionGuard>

              {/* Files List */}
              <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-dark-border">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <File className="w-4 h-4 text-primary-500 dark:text-accent-400" />
                    Contract Files & Versions
                  </h3>
                </div>
                <div className="p-5">
                  {isLoadingFiles ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 dark:border-accent-400"></div>
                      <span className="ml-2 text-gray-600 dark:text-gray-400">Loading files...</span>
                    </div>
                  ) : files && files.length > 0 ? (
                    <div className="space-y-3">
                      {files.map((file) => (
                        <div key={file.id} className="bg-gray-50 dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-primary-50 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
                                <FileIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900 dark:text-white">{file.filename}</h4>
                                <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                                  <span>{formatFileSize(file.fileSize)}</span>
                                  <span>•</span>
                                  <span>Uploaded by {file.uploadedBy.name}</span>
                                  <span>•</span>
                                  <span>{formatDate(new Date(file.uploadedAt))}</span>
                                </div>
                                {file.versionLabel && (
                                  <div className="flex items-center gap-2 mt-2">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{file.versionLabel}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-lg font-medium ${getVersionTypeColor(file.versionType || 'draft')}`}>
                                      {file.versionType || 'draft'}
                                    </span>
                                  </div>
                                )}
                                {file.notes && (
                                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{file.notes}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDownloadFile(file.id, file.filename)}
                                className="p-2 text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-surface-raised rounded-lg transition-colors"
                                title="Download"
                              >
                                <Download className="w-5 h-5" />
                              </button>
                              <PermissionGuard permission="contracts.files.delete">
                                {onFileDelete && (
                                  <button
                                    onClick={async () => {
                                      if (window.confirm(`Are you sure you want to delete "${file.filename}"? This action cannot be undone.`)) {
                                        await onFileDelete(contract.id, file.id);
                                        // Refresh files list after deletion
                                        await fetchContractFiles();
                                      }
                                    }}
                                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-error-600 dark:hover:text-error-400 hover:bg-gray-100 dark:hover:bg-dark-surface-raised rounded-lg transition-colors"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                )}
                              </PermissionGuard>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 mx-auto bg-gray-100 dark:bg-dark-bg-secondary rounded-xl flex items-center justify-center mb-3">
                        <Paperclip className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 font-medium">No files uploaded yet</p>
                      <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">Upload your first contract version above</p>
                    </div>
                  )}
                </div>
              </div>
              </div>
            </PermissionGuard>
          )}

          {activeSection === 'notes' && (
            <PermissionGuard permission="contracts.notes.view">
              <div className="space-y-6">
              {/* Add Note Section */}
              <PermissionGuard permission="contracts.notes.create">
                <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border">
                  <div className="px-5 py-4 border-b border-gray-200 dark:border-dark-border">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-primary-500 dark:text-accent-400" />
                      Add Internal Note
                    </h3>
                  </div>
                  <div className="p-5 space-y-4">
                    <RichTextEditor
                      value={newNote}
                      onChange={setNewNote}
                      placeholder="Add a note about this contract..."
                      style={{
                        minHeight: '160px'
                      }}
                    />
                    <button
                      onClick={handleNoteAdd}
                      disabled={!newNote.replace(/<[^>]*>/g, '').trim()}
                      className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 rounded-lg hover:shadow-lg hover:shadow-primary-500/25 dark:hover:shadow-accent-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                    >
                      <MessageSquare className="w-4 h-4" />
                      Add Note
                    </button>
                  </div>
                </div>
              </PermissionGuard>

              {/* Notes List */}
              <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-dark-border">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-primary-500 dark:text-accent-400" />
                    Internal Notes & Comments
                  </h3>
                </div>
                <div className="p-5">
                  {isLoadingNotes ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 dark:border-accent-400"></div>
                      <span className="ml-2 text-gray-600 dark:text-gray-400">Loading notes...</span>
                    </div>
                  ) : notes && notes.length > 0 ? (
                    <div className="space-y-4">
                      {notes.map((note) => (
                        <div key={note.id} className="bg-gray-50 dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 flex-1">
                              <div className="w-9 h-9 bg-primary-50 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
                                <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-gray-900 dark:text-white">{note.createdBy.name}</span>
                                  <span className="text-sm text-gray-500 dark:text-gray-400">
                                    {formatRelativeTime(new Date(note.createdAt))}
                                  </span>
                                  {note.isEdited && (
                                    <span className="text-xs text-gray-400 dark:text-gray-500">(edited)</span>
                                  )}
                                </div>
                                {editingNoteId === note.id ? (
                                  <div className="space-y-3">
                                    <RichTextEditor
                                      value={editingNoteContent}
                                      onChange={setEditingNoteContent}
                                      style={{
                                        minHeight: '140px'
                                      }}
                                    />
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => handleNoteEdit(note.id)}
                                        className="px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-500 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 text-sm rounded-lg font-medium hover:shadow-md transition-all"
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={() => {
                                          setEditingNoteId(null);
                                          setEditingNoteContent('');
                                        }}
                                        className="px-4 py-2 bg-gray-100 dark:bg-dark-surface-raised text-gray-700 dark:text-gray-300 text-sm rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-dark-border transition-colors"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div
                                    className="text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none text-left"
                                    style={{ textAlign: 'left', direction: 'ltr' }}
                                    dangerouslySetInnerHTML={{ __html: note.content }}
                                  />
                                )}
                              </div>
                            </div>
                            {!editingNoteId && (
                              <div className="flex items-center gap-1 ml-4">
                                <PermissionGuard permission="contracts.notes.edit">
                                  {onNoteEdit && (
                                    <button
                                      onClick={() => {
                                        setEditingNoteId(note.id);
                                        setEditingNoteContent(note.content);
                                      }}
                                      className="p-2 text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-surface-raised rounded-lg transition-colors"
                                      title="Edit"
                                    >
                                      <Edit3 className="w-4 h-4" />
                                    </button>
                                  )}
                                </PermissionGuard>
                                <PermissionGuard permission="contracts.notes.delete">
                                  {onNoteDelete && (
                                    <button
                                      onClick={async () => {
                                        if (window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
                                          await onNoteDelete(contract.id, note.id);
                                          // Refresh notes list to remove the deleted note
                                          await fetchContractNotes();
                                        }
                                      }}
                                      className="p-2 text-gray-400 dark:text-gray-500 hover:text-error-600 dark:hover:text-error-400 hover:bg-gray-100 dark:hover:bg-dark-surface-raised rounded-lg transition-colors"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </PermissionGuard>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 mx-auto bg-gray-100 dark:bg-dark-bg-secondary rounded-xl flex items-center justify-center mb-3">
                        <MessageSquare className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 font-medium">No notes yet</p>
                      <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">Add your first note above</p>
                    </div>
                  )}
                </div>
              </div>
              </div>
            </PermissionGuard>
          )}

          {activeSection === 'negotiation' && (contract as any).negotiationEmail && (() => {
            const email = (contract as any).negotiationEmail;
            const decodedSubject = decodeHtmlEntities(email.subject);
            const decodedBody = decodeHtmlEntities(email.body);

            return (
            <div className="space-y-6">
              {/* Email Preview Card */}
              <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border shadow-sm overflow-hidden">
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-primary-600 to-primary-500 dark:from-accent-600 dark:to-accent-500 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/20 rounded-lg">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          Negotiation Email
                        </h3>
                        <p className="text-sm text-white/80">
                          Generated on {formatDate(new Date(email.generatedAt))}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(decodedBody);
                          setCopiedEmail(true);
                          setTimeout(() => setCopiedEmail(false), 2000);
                        }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                          copiedEmail
                            ? 'bg-green-500 text-white'
                            : 'bg-white/20 text-white hover:bg-white/30'
                        }`}
                      >
                        {copiedEmail ? (
                          <>
                            <Check className="w-4 h-4" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Subject Line */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-secondary">
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
                    <span className="font-medium">Subject:</span>
                  </div>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">
                    {decodedSubject}
                  </p>
                </div>

                {/* Email Body */}
                <div className="px-6 py-5">
                  <div className="bg-gray-50 dark:bg-dark-bg-secondary rounded-xl border border-gray-200 dark:border-dark-border overflow-hidden">
                    <div className="max-h-[500px] overflow-y-auto p-6">
                      <div className="prose prose-sm dark:prose-invert max-w-none text-left">
                        <div className="whitespace-pre-wrap font-sans text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                          {decodedBody}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="px-6 py-4 bg-gray-50 dark:bg-dark-bg-secondary border-t border-gray-200 dark:border-dark-border">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        const mailtoLink = `mailto:?subject=${encodeURIComponent(decodedSubject || 'Contract Negotiation')}&body=${encodeURIComponent(decodedBody)}`;
                        window.open(mailtoLink);
                      }}
                      className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 dark:bg-accent-500 dark:hover:bg-accent-600 text-white dark:text-gray-900 rounded-lg transition-colors font-medium shadow-sm"
                    >
                      <Mail className="w-4 h-4" />
                      Open in Email Client
                    </button>
                    <button
                      onClick={() => {
                        const blob = new Blob([decodedBody], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `negotiation-email-${contract.contractNumber || contract.id}.txt`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-dark-surface text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-100 dark:hover:bg-dark-surface-raised transition-colors font-medium"
                    >
                      <Download className="w-4 h-4" />
                      Download as Text
                    </button>
                  </div>
                </div>
              </div>
            </div>
            );
          })()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg hover:bg-gray-200 dark:hover:bg-dark-surface-raised transition-colors font-medium"
          >
            Close
          </button>
          {activeSection === 'info' && (
            <div className="flex gap-3">
              <PermissionGuard permission="contracts.approve">
                {onReview && contract && (
                  <button
                    onClick={() => onReview(contract)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800/50 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors font-medium"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Review Contract
                  </button>
                )}
              </PermissionGuard>
              <PermissionGuard permission="contracts.edit">
                {onEdit && (
                  <button
                    onClick={() => onEdit(contract)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 rounded-lg hover:shadow-lg hover:shadow-primary-500/25 dark:hover:shadow-accent-500/25 transition-all font-medium"
                  >
                    <Edit3 className="w-4 h-4" />
                    Edit Contract
                  </button>
                )}
              </PermissionGuard>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractDetailsModal; 